import { useQuery } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data';
import { getAGGClient, isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';

import { EotBannerPaymentDetailsQuery } from './graphql/EotBannerPaymentDetailsQuery.agggraphql';
import type { EotBannerPaymentDetailsQuery as EotBannerPaymentDetailsQueryType } from './graphql/__types__/EotBannerPaymentDetailsQuery';

interface PaymentDetails {
	/** When true, then the rest of the fields in this object are ready for consumption */
	isPaymentDataAvailable: boolean;
	/** Does the entitlement already have a payment set up? */
	isPaymentOnFile: boolean;
	/** A deep link for the user to add payment */
	paymentFlowUrl?: string;
	/** Can the current user provide payment details? */
	canUserPay: boolean;
}

const NO_RESULT = {
	isPaymentDataAvailable: false,
	isPaymentOnFile: false,
	canUserPay: false,
};

/**
 * Returns pertinent payment information for the entitlement and user.
 *
 * @param skip When true, the AGG call is not made.
 */
export function usePaymentDetails(skip: boolean): PaymentDetails {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { cloudId } = useSessionData();

	const { data, loading, error } = useQuery<EotBannerPaymentDetailsQueryType>(
		EotBannerPaymentDetailsQuery,
		{
			client: getAGGClient(),
			variables: {
				cloudId,
			},
			skip,
		},
	);

	if (error && !isErrorMarkedAsHandled(error)) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'usePaymentDetails',
				source: 'EotBanner',
				attributes: {
					growthFeature: 'EotBanner',
					errorMessage: error.message,
				},
			},
		}).fire();

		markErrorAsHandled(error);
	}

	if (skip || loading || error || !data || !data.tenantContexts) {
		return NO_RESULT;
	}

	const isPaymentOnFile =
		data.tenantContexts[0]?.entitlementInfo?.entitlement?.subscription?.accountDetails?.invoiceGroup
			?.invoiceable ?? false;
	const paymentFlowUrl =
		data.tenantContexts[0]?.entitlementInfo?.entitlement?.subscription?.accountDetails?.invoiceGroup
			?.experienceCapabilities?.configurePaymentV2?.experienceUrl ?? '';
	const canUserPay =
		data.tenantContexts[0]?.entitlementInfo?.entitlement?.subscription?.accountDetails?.invoiceGroup
			?.experienceCapabilities?.configurePaymentV2?.isAvailableToUser ?? false;

	return {
		isPaymentDataAvailable: true,
		isPaymentOnFile,
		paymentFlowUrl,
		canUserPay,
	};
}
